import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowRight, CircleAlert, Lock, Mail, Plane, Ship, Train, Truck } from "lucide-react";
import { useRuntime } from "../runtime/RuntimeContext";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { useDocumentTitle } from "../hooks/useDocumentTitle";

// Only honour internal, non-protocol-relative paths — never an open redirect.
function safeRedirect(target: unknown): string {
    if (typeof target !== "string" || !target.startsWith("/")) return "/wc";
    if (target.startsWith("//") || target.startsWith("/\\")) return "/wc";
    return target;
}

// Canonical login layout (brand panel + sign-in form), themed via the .login__*
// classes from theme.css. Wired to the real AuthService + forgot-password route.
export function LoginPage() {
    useDocumentTitle("Sign in");
    const runtime = useRuntime();
    const navigate = useNavigate();
    const search = useSearch({ strict: false }) as { redirect?: string };
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [rememberMe, setRememberMe] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        try {
            await runtime.services.auth.login(email, password);
            navigate({ to: safeRedirect(search.redirect) } as Parameters<typeof navigate>[0]);
        } catch {
            setError("Sign in failed. Check your credentials and try again.");
            setIsLoading(false);
        }
    };

    return (
        <div className="login">
            <aside className="login__brand-side">
                <svg
                    className="login__network"
                    xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="none"
                    viewBox="0 0 600 800"
                >
                    <line x1="0" y1="200" x2="600" y2="400" stroke="white" strokeWidth="1.5" />
                    <line x1="0" y1="500" x2="600" y2="200" stroke="white" strokeWidth="1.5" />
                    <line x1="150" y1="0" x2="300" y2="800" stroke="white" strokeWidth="1.5" />
                    <line x1="450" y1="0" x2="200" y2="800" stroke="white" strokeWidth="1.5" />
                    <line x1="0" y1="350" x2="600" y2="350" stroke="white" strokeWidth="1" />
                    <line x1="300" y1="0" x2="300" y2="800" stroke="white" strokeWidth="1" />
                    {[[150, 200], [300, 350], [450, 200], [200, 500], [400, 500], [80, 350], [520, 350], [300, 100], [300, 600]].map(
                        ([cx, cy], i) => (
                            <circle key={i} cx={cx} cy={cy} r="6" fill="white" />
                        ),
                    )}
                    <circle cx="300" cy="350" r="12" fill="white" />
                    <circle cx="150" cy="200" r="10" fill="white" />
                    <circle cx="450" cy="500" r="10" fill="white" />
                </svg>

                <div className="login__orb login__orb--br" />
                <div className="login__orb login__orb--tl" />

                <div className="login__brand-content">
                    <div className="login__brand-icon">
                        <Ship size={28} />
                    </div>

                    <h1 className="login__hero">
                        Global <span className="login__hero-accent">Logistics</span> &amp;
                        <br />
                        <span className="login__hero-accent">Freight</span> Forwarding.
                    </h1>

                    <p className="login__tagline">
                        Manage quotes, bookings, tracking, schedules, equipment control, multi-leg shipments — all from one
                        intelligent freight forwarding platform.
                    </p>

                    <div className="login__modes">
                        {[
                            { Icon: Plane, label: "Air" },
                            { Icon: Ship, label: "Sea" },
                            { Icon: Truck, label: "Road" },
                            { Icon: Train, label: "Rail" },
                        ].map(({ Icon, label }) => (
                            <div key={label} className="login__mode-pill">
                                <Icon size={14} />
                                <span>{label}</span>
                            </div>
                        ))}
                    </div>

                    <div className="login__powered">Powered by EDE Platform</div>
                </div>
            </aside>

            <main className="login__form-side">
                <div className="login__form-wrap">
                    <header className="login__form-header">
                        <h2>Sign In</h2>
                        <p>Enter your credentials to access your workspace</p>
                    </header>

                    <form onSubmit={handleSubmit} className="login__form">
                        <div className="login__field">
                            <label htmlFor="login-email">Email Address</label>
                            <div className="login__input">
                                <Mail />
                                <input
                                    id="login-email"
                                    type="email"
                                    required
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="name@company.com"
                                />
                            </div>
                        </div>

                        <div className="login__field">
                            <div className="login__field-row">
                                <label htmlFor="login-password">Password</label>
                                <Link to="/auth/forgot-password" className="login__forgot">
                                    Forgot password?
                                </Link>
                            </div>
                            <div className="login__input">
                                <Lock />
                                <input
                                    id="login-password"
                                    type="password"
                                    required
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                />
                            </div>
                        </div>

                        <label className="login__remember">
                            <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
                            <span>Remember me</span>
                        </label>

                        {error ? (
                            <Alert variant="destructive">
                                <CircleAlert />
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        ) : null}

                        <button type="submit" disabled={isLoading} className="login__submit">
                            <span>{isLoading ? "Signing in…" : "Sign In to your Workspace"}</span>
                            {!isLoading ? <ArrowRight size={16} /> : null}
                        </button>
                    </form>
                </div>
            </main>
        </div>
    );
}
